import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { SEO } from "@/components/SEO";
import { TaskStatusSelect } from "@/components/TaskStatusDropdown";
import { TaskPrioritySelect } from "@/components/TaskPriorityDropdown";
import { TaskAssigneeSelect } from "@/components/TaskAssigneeDropdown";
import { DueDateSelect } from "@/components/DueDateSelect";
import TaskLabels from "@/components/TaskDetails/TaskLabels";
import { STATUS_COLORS } from "@/lib/colorsMap";
import TipTapEditor from "@/components/Editor/TiptapEditor";
import { AttachmentProvider, AttachmentUploadButton, AttachmentList, AttachmentDeleteModal } from "@/components/TaskDetails/TaskAttachments";
import { FiChevronRight, FiCheck } from 'react-icons/fi';
import StatusBotIcon from "@/components/StatusBotIcon";

export function CreateTaskDialog({ open, onOpenChange, project, projects, onTaskCreated, initialData, generatingTask, createdFromSubmission }) {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedProject, setSelectedProject] = useState(project);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
    const projectDropdownRef = useRef(null);
    const descriptionEditorRef = useRef(null);

    // Use selectedProject for all project-related logic
    const currentProject = selectedProject || project;

    // Update selected project when project prop changes
    useEffect(() => {
        if (project && !selectedProject) {
            setSelectedProject(project);
        }
        console.log('currentProject:', currentProject);
    }, [project, selectedProject]);

    // Convert project statuses to the format expected by TaskStatusSelect
    const statuses = currentProject?.statuses?.reduce((acc, status) => ({
        ...acc,
        [status.id]: {
            name: status.name,
            icon: status.icon,
            color: STATUS_COLORS[status.color]?.text || `text-${status.color}-500`
        }
    }), {});

    const priorities = {
        no_priority: { name: 'No Priority', icon: 'signal-none', color: 'text-neutral-500' },
        low: { name: 'Low', icon: 'signal-low', color: 'text-violet-500' },
        medium: { name: 'Medium', icon: 'signal-medium', color: 'text-orange-500' },
        high: { name: 'High', icon: 'signal-full', color: 'text-red-500' },
        urgent: { name: 'Urgent', icon: 'alert-circle', color: 'text-red-500' }
    };

    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axiosInstance.get(`/organizations/${currentProject.organizationSlug}/users`);
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
                setUsers([]);
            }
        };

        if (open && currentProject?.organizationSlug) {
            fetchUsers();
        }
    }, [open, currentProject?.organizationSlug]);

    const [formData, setFormData] = useState({
        summary: initialData?.summary || '',
        description: initialData?.description || '',
        status: initialData?.status || 'todo',
        priority: initialData?.priority || 'no_priority',
        assigneeId: initialData?.assigneeId || null,
        dueDate: initialData?.dueDate || null,
        labels: initialData?.labels || []
    });

    // Update formData when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                summary: initialData.summary || prev.summary,
                description: initialData.description || prev.description,
                priority: initialData.priority || prev.priority,
                assigneeId: initialData.assigneeId || prev.assigneeId,
                dueDate: initialData.dueDate || prev.dueDate,
                labels: initialData.labels || prev.labels
            }));
            
            // Set the TipTap JSON content directly (server converts markdown to TipTap JSON)
            if (initialData.description && descriptionEditorRef.current) {
                descriptionEditorRef.current.commands.setContent(initialData.description);
            }

            // Handle submission files - show them as preloaded attachments
            if (initialData.submissionFiles && Array.isArray(initialData.submissionFiles)) {
                console.log('Loading submission files:', initialData.submissionFiles);
                setUploadedFiles(initialData.submissionFiles);
            }
        }
    }, [initialData]);

    const onFileUpload = async (file) => {
        try {
            const uploadId = Math.random().toString(36).substring(7);
            // Create a temporary file object to store in state
            const tempFile = {
                id: uploadId,
                name: file.name,
                size: file.size,
                type: file.type,
                file: file,
                alt: file.name,
                uploadId: uploadId,
                url: URL.createObjectURL(file)
            };

            // Add to uploaded files state
            setUploadedFiles(prev => [...prev, tempFile]);

            // Return a temporary response that matches the expected format
            return {
                data: {
                    attachments: [{
                        url: tempFile.url,
                        filename: tempFile.name,
                        key: tempFile.id,
                        uploadId: uploadId  // Add this to track the file
                    }]
                }
            };
        } catch (error) {
            console.error('Error handling file upload:', error);
            toast({
                description: "Failed to handle file upload",
                variant: "destructive"
            });
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Filter submission files that are still in uploadedFiles (not removed by user)
            const remainingSubmissionFiles = uploadedFiles
                .filter(file => file.fromSubmission)
                .map(file => ({
                    key: file.key,
                    filename: file.filename || file.name,
                    contentType: file.contentType || file.type
                }));

            // Prepare createdFromSubmission data with only remaining files
            const submissionData = createdFromSubmission ? {
                ...createdFromSubmission,
                filesToCopy: remainingSubmissionFiles // Only copy files that weren't removed
            } : null;

            // First create the task
            const taskResponse = await axiosInstance.post(`/tasks/${currentProject?.organizationSlug}`, {
                ...formData,
                projectId: currentProject?.id,
                createdFromSubmission: submissionData
            });

            if (taskResponse && taskResponse.data) {
                const taskKey = taskResponse.data.key;

                // Filter out submission files - they are already copied on the server side
                // Only upload files that have an actual File object and are not from submission
                const filesToUpload = uploadedFiles.filter(file => file.file && !file.fromSubmission);

                // Upload only newly added files (not submission files)
                if (filesToUpload.length > 0) {
                    const uploadPromises = filesToUpload.map((file) => {
                        const formData = new FormData();
                        formData.append('file', file.file); // Use the actual file from the tempFile object
                        return axiosInstance.post(
                            `/tasks/${currentProject?.organizationSlug}/${taskKey}/attachments?uploadId=${file.uploadId}`,
                            formData
                        );
                    });

                    const uploadResponses = await Promise.all(uploadPromises);
                    // Find the response with the most attachments
                    const allAttachments = uploadResponses.reduce((longest, response) => {
                        const currentAttachments = response.data?.attachments || [];
                        return currentAttachments.length > longest.length ? currentAttachments : longest;
                    }, []);
                    // Update the description with the correct URLs
                    const updatedDescription = { ...formData.description };
                    if (updatedDescription.content) {
                        updatedDescription.content = updatedDescription.content.map(node => {
                            if (node.type === 's3Image' && node.attrs) {
                                // Find the matching file from the upload responses using the uploadId
                                const uploadedFile = allAttachments.find(attachment => {
                                    return attachment.filename === node.attrs.alt;
                                });

                                if (uploadedFile) {
                                    return {
                                        ...node,
                                        attrs: {
                                            ...node.attrs,
                                            src: uploadedFile.url,
                                            key: uploadedFile.key,
                                            uploadId: null // Clear the uploadId since upload is complete
                                        }
                                    };
                                }
                            }
                            return node;
                        });
                    }

                    // Update the task with the new description
                    await axiosInstance.patch(
                        `/tasks/${currentProject?.organizationSlug}/${taskKey}`,
                        { description: updatedDescription }
                    );
                }

                onOpenChange(false);
                toast({
                    description: (
                        <button 
                            onClick={() => navigate(`/${currentProject?.organizationSlug}/plan/${taskKey}`)}
                            className="text-primary underline hover:no-underline font-medium bg-transparent border-none cursor-pointer p-0 text-left"
                        >
                            {taskKey} created successfully
                        </button>
                    ),
                });

                if (onTaskCreated) onTaskCreated({
                    id: taskResponse.data._id,
                    key: taskResponse.data.key
                });

                uploadedFiles.forEach(file => {
                    if (file.url && file.url.startsWith('blob:')) {
                        URL.revokeObjectURL(file.url);
                    }
                });

                // Reset form
                setFormData({
                    summary: '',
                    description: '',
                    status: 'todo',
                    priority: 'no_priority',
                    assigneeId: null,
                    dueDate: null,
                    labels: []
                });
                setUploadedFiles([]);
            }
        } catch (error) {
            console.error('Error creating task:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to create task",
                variant: "destructive"
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
                setProjectDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Close dropdown when dialog closes
    useEffect(() => {
        if (!open) {
            setProjectDropdownOpen(false);
        }
    }, [open]);

    // Remove the separate submission/other files logic - just use all uploadedFiles
    const handleTaskAttachmentsChange = (updatedTask) => {
        setUploadedFiles(updatedTask.attachments);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {open && currentProject &&
                <SEO title={`${currentProject.name} › New task`} />
            }
            
            <DialogContent className="max-w-md md:max-w-3xl grid grid-cols-1">
                {generatingTask && (
                    <div className="absolute inset-0 z-[100] flex items-center justify-center w-full h-full bg-background/70 backdrop-blur-sm rounded-lg p-8 pointer-events-auto">
                        <div className="text-center flex items-center gap-3">
                            <StatusBotIcon className="h-6 w-6 text-teal-500" />
                            <div className="relative inline-block bg-radial from-black/40 from-40% to-black/80 to-60% dark:from-white/40 dark:from-40% dark:to-white/80 dark:to-60% bg-clip-text text-transparent animate-shimmer text-sm">
                                StatusBot is generating your task
                            </div>
                        </div>
                    </div>
                )}
                <div className="relative w-full h-full">
                    <DialogHeader className="pb-3">
                        <DialogTitle className="flex items-center gap-2">
                            {projects && projects.length > 1 ? (
                                <div className="relative" ref={projectDropdownRef}>
                                    <Button 
                                        variant="secondary"
                                        className="h-8 px-3 text-sm font-medium w-auto flex items-center gap-1.5 hover:bg-secondary/80 transition-colors"
                                        onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                                    >
                                        <span>{currentProject?.name || "Select project"}</span>
                                    </Button>
                                    
                                    {projectDropdownOpen && (
                                        <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 min-w-max animate-in fade-in-0 zoom-in-95 duration-200">
                                            <div className="p-1">
                                                {projects.map((project) => (
                                                    <button
                                                        key={project.id}
                                                        className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-between gap-3"
                                                        onClick={() => {
                                                            setSelectedProject(project);
                                                            setProjectDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span className="whitespace-nowrap">{project.name}</span>
                                                        {selectedProject?.id === project.id && (
                                                            <FiCheck className="w-3 h-3 text-primary" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button 
                                    variant="secondary"
                                    disabled
                                    className="h-8 px-3 text-sm font-medium w-auto"
                                >
                                    {currentProject?.name || "Project"}
                                </Button>
                            )}
                            <FiChevronRight 
                                className={`w-3 h-3 transition-transform duration-200 ${
                                    projectDropdownOpen ? 'rotate-90' : ''
                                }`} 
                            />
                            <span className="text-base font-normal text-neutral-600 dark:text-neutral-400">New task</span>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit}>
                        <AttachmentProvider 
                            task={{ attachments: uploadedFiles }} 
                            onTaskChange={handleTaskAttachmentsChange} 
                            descriptionEditorRef={descriptionEditorRef} 
                            newTask={true}
                        >
                            <Input
                                id="summary"
                                value={formData.summary}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    summary: e.target.value
                                }))}
                                required
                                placeholder="Task summary"
                                className="placeholder:text-neutral-500 dark:placeholder:text-neutral-600 text-lg font-semibold focus:outline-none py-2 min-h-11 border-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent p-0"
                            />
                            {/* Scrollable div for the editor */}
                            <div 
                                className="overflow-y-auto"
                                style={{
                                    maxHeight: `calc(90vh - ${255 + (uploadedFiles.length * 28)}px)`
                                }}
                            >
                                <TipTapEditor
                                    id="description"
                                    content={formData.description}
                                    placeholder="Add description..."
                                    className="min-h-24"
                                    withMentions
                                    withCodeBlocks
                                    withBubbleMenu
                                    withS3Images
                                    withS3Uploads
                                    organizationSlug={currentProject?.organizationSlug}
                                    onFileUpload={onFileUpload}
                                    onUpdate={({ editor }) => {
                                        const json = editor.getJSON();
                                        // Check for removed images
                                        if (json.content) {
                                            const currentImageAttrs = json.content
                                                .filter(node => node.type === 's3Image' && node.attrs)
                                                .map(node => node.attrs.alt);

                                            // Remove files that are no longer in the description
                                            setUploadedFiles(prev => prev.filter(file =>
                                                currentImageAttrs.includes(file.name)
                                            ));
                                        }
                                        setFormData(prev => ({
                                            ...prev,
                                            description: json
                                        }));
                                    }}
                                    ref={descriptionEditorRef}
                                    onEditorReady={(editor) => {
                                        descriptionEditorRef.current = editor;
                                    }}
                                />
                            </div>

                            {/* Attachment list between description and task settings */}
                            <div className="mt-4 mb-4 pt-4">
                                <AttachmentList style="minimal" />
                            </div>

                            {/* Status, Priority, and Assignee in a row */}
                            <div className="flex flex-wrap gap-2">
                                {statuses &&
                                    <TaskStatusSelect
                                        statuses={statuses}
                                        value={formData.status}
                                        onChange={(status) => setFormData(prev => ({
                                            ...prev,
                                            status
                                        }))}
                                    />
                                }
                                {priorities &&
                                    <TaskPrioritySelect
                                        priorities={priorities}
                                        value={formData.priority}
                                        onChange={(priority) => setFormData(prev => ({
                                            ...prev,
                                            priority
                                        }))}
                                    />
                                }
                                {users &&
                                    <TaskAssigneeSelect
                                        users={users}
                                        value={formData.assigneeId}
                                        onChange={(assigneeId) => setFormData(prev => ({
                                            ...prev,
                                            assigneeId
                                        }))}
                                        showName
                                    />
                                }
                                <DueDateSelect
                                    task={formData}
                                    showLabel={false}
                                    newTask={true}
                                    organizationSlug={currentProject?.organizationSlug}
                                    onTaskChange={(updatedTask) => setFormData(prev => ({
                                        ...prev,
                                        dueDate: updatedTask.dueDate
                                    }))}
                                />
                                <TaskLabels
                                    task={formData}
                                    showLabel={false}
                                    newTask={true}
                                    newTaskAvailableLabels={currentProject?.labels || []}
                                    organizationSlug={currentProject?.organizationSlug}
                                    onTaskChange={(updatedTask) => setFormData(prev => ({
                                        ...prev,
                                        labels: updatedTask.labels
                                    }))}
                                />
                            </div>

                            <DialogFooter className="border-t border-neutral-200 dark:border-neutral-800 pt-5 mt-5 flex flex-row justify-between sm:justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <AttachmentUploadButton />
                                </div>
                                <div className="flex flex-col md:flex-row md:justify-between space-x-4">
                                    <Button type="submit" disabled={submitting}>
                                        {submitting ? "Creating..." : "Create task"}
                                    </Button>
                                </div>
                            </DialogFooter>

                            <AttachmentDeleteModal />
                        </AttachmentProvider>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}